import Card from "components/card";
import Transaction from "components/dataDisplay/Transaction";
import React, { useState } from "react";
import {
  MdLightbulbOutline,
  MdEmail,
  MdGroup,
  MdRocketLaunch,
  MdBuild,
  MdSchool,
  MdTimeline,
  MdCalendarToday,
  MdWork,
  MdCall,
  MdAccessTime,
  MdPsychology,
  MdSelfImprovement,
  MdHandshake,
  MdChat,
  MdVideoCall,
  MdChevronLeft,
  MdChevronRight,
} from 'react-icons/md';
import { ProfileStats } from 'utils/dashboardDataHelper';

const getCustomIcon = (label: string) => {
  const lower = label.toLowerCase();

  if (lower.includes("email")) return <MdEmail className="text-blue-500" />;
  if (lower.includes("friend")) return <MdGroup className="text-pink-500" />;
  if (lower.includes("irl") || lower.includes("event")) return <MdCalendarToday className="text-teal-500" />;
  
  if (lower.includes("kickstart")) return <MdRocketLaunch className="text-red-500" />;
  if (lower.includes("project")) return <MdBuild className="text-yellow-500" />;
  if (lower.includes("academic")) return <MdSchool className="text-indigo-500" />;
  if (lower.includes("internship") || lower.includes("job")) return <MdWork className="text-cyan-500" />;

  if (lower.includes("confidence")) return <MdSelfImprovement className="text-orange-500" />;
  if (lower.includes("career")) return <MdRocketLaunch className="text-lime-500" />;
  if (lower.includes("introductions")) return <MdHandshake className="text-fuchsia-500" />;
  if (lower.includes("skills")) return <MdPsychology className="text-purple-500" />;

  if (lower.includes("semester")) return <MdTimeline className="text-green-500" />;
  if (lower.includes("month")) return <MdCalendarToday className="text-orange-500" />;
  if (lower.includes("week")) return <MdAccessTime className="text-gray-500" />;

  if (lower.includes("call")) return <MdCall className="text-purple-500" />;
  if (lower.includes("message")) return <MdChat className="text-blue-400" />;
  if (lower.includes("virtual") || lower.includes("meeting")) return <MdVideoCall className="text-violet-500" />;

  return <MdLightbulbOutline className="text-gray-400" />;
};

function ProfileInsights() {
  // Pagination state for each category
  const [categoryPages, setCategoryPages] = useState<Record<string, number>>({});
  
  const [profileStats] = useState<ProfileStats>({
    totalProfiles: 5127,
    mentorCount: 1542,
    menteeCount: 3585,
    usersPerMonth: [],
    fieldCounts: {
      userObjectives: {},
      userGoals: {},
      userSources: {},
      userStreaks: {},
      prefAIChat: {},
      prefMentorshipChat: {},
    },
    topEntries: [
      { label: "Friends/Network", count: 1847, category: "Sources" },
      { label: "Email from College", count: 1234, category: "Sources" },
      { label: "IRL Events", count: 987, category: "Sources" },
      { label: "Social Media", count: 789, category: "Sources" },
      { label: "University Portal", count: 654, category: "Sources" },
      { label: "LinkedIn", count: 543, category: "Sources" },
      { label: "Career Fair", count: 432, category: "Sources" },
      { label: "Professor Referral", count: 321, category: "Sources" },
      
      { label: "Career Kickstart", count: 2341, category: "Objectives" },
      { label: "Project Building", count: 1567, category: "Objectives" },
      { label: "Academic Support", count: 1219, category: "Objectives" },
      { label: "Internship Prep", count: 987, category: "Objectives" },
      { label: "Job Search", count: 876, category: "Objectives" },
      { label: "Skill Development", count: 743, category: "Objectives" },
      { label: "Interview Practice", count: 654, category: "Objectives" },
      { label: "Resume Review", count: 543, category: "Objectives" },
      
      { label: "Building Confidence", count: 1834, category: "Goals" },
      { label: "Career Planning", count: 1623, category: "Goals" },
      { label: "Better Introductions", count: 1270, category: "Goals" },
      { label: "Technical Skills", count: 1400, category: "Goals" },
      { label: "Leadership Development", count: 876, category: "Goals" },
      { label: "Public Speaking", count: 743, category: "Goals" },
      { label: "Work-Life Balance", count: 654, category: "Goals" },
      { label: "Entrepreneurship", count: 567, category: "Goals" },
      { label: "Industry Knowledge", count: 456, category: "Goals" },
      { label: "Presentation Skills", count: 345, category: "Goals" },
      
      { label: "This Semester", count: 2134, category: "Timeline" },
      { label: "This Month", count: 1789, category: "Timeline" },
      { label: "This Week", count: 1204, category: "Timeline" },
      { label: "Next 3 Months", count: 876, category: "Timeline" },
      { label: "6 Months", count: 654, category: "Timeline" },
      { label: "1 Year", count: 432, category: "Timeline" },
      
      { label: "Virtual Meetings", count: 2987, category: "Communication" },
      { label: "Chat Messages", count: 1834, category: "Communication" },
      { label: "Phone Calls", count: 306, category: "Communication" },
      { label: "Email Follow-ups", count: 234, category: "Communication" },
      { label: "Group Sessions", count: 198, category: "Communication" },
      
      { label: "Engineering", count: 1234, category: "Field" },
      { label: "Business", count: 987, category: "Field" },
      { label: "Computer Science", count: 876, category: "Field" },
      { label: "Finance", count: 654, category: "Field" },
      { label: "Marketing", count: 543, category: "Field" },
      { label: "Healthcare", count: 432, category: "Field" },
      { label: "Design", count: 321, category: "Field" },
      { label: "Data Science", count: 298, category: "Field" },
      { label: "Consulting", count: 267, category: "Field" },
      { label: "Research", count: 234, category: "Field" },
      
      { label: "Undergraduate", count: 2876, category: "Level" },
      { label: "Graduate", count: 1543, category: "Level" },
      { label: "PhD", count: 432, category: "Level" },
      { label: "Professional", count: 276, category: "Level" },
      
      { label: "First Generation", count: 789, category: "Background" },
      { label: "International Student", count: 654, category: "Background" },
      { label: "Career Changer", count: 432, category: "Background" },
      { label: "Returning Student", count: 234, category: "Background" },
    ],
  });

  const ITEMS_PER_PAGE = 6;

  // Get current page for a category (default to 0)
  const getCurrentPage = (category: string) => categoryPages[category] || 0;

  // Navigate to next/previous page for a category
  const navigatePage = (category: string, direction: 'next' | 'prev') => {
    const currentPage = getCurrentPage(category);
    const categoryEntries = profileStats.topEntries.filter(entry => entry.category === category);
    const totalPages = Math.ceil(categoryEntries.length / ITEMS_PER_PAGE);

    let newPage = currentPage;
    if (direction === 'next' && currentPage < totalPages - 1) {
      newPage = currentPage + 1;
    } else if (direction === 'prev' && currentPage > 0) {
      newPage = currentPage - 1;
    }

    setCategoryPages(prev => ({ ...prev, [category]: newPage }));
  };

  // Get paginated items for a category
  const getPaginatedItems = (category: string) => {
    const categoryEntries = profileStats.topEntries.filter(entry => entry.category === category);
    const currentPage = getCurrentPage(category);
    const startIndex = currentPage * ITEMS_PER_PAGE;
    const endIndex = startIndex + ITEMS_PER_PAGE;
    return categoryEntries.slice(startIndex, endIndex);
  };

  return (
    <Card extra={"w-full px-3 pt-3 2xl:pb-5 flex flex-col overflow-hidden h-[1510px]"}>
      {/* Header */}
      <div className="flex items-center justify-between px-3 pt-3 flex-shrink-0">
        <h4 className="text-lg font-bold text-navy-700 dark:text-white">
          Profiles Insights
          <p className="text-sm font-medium text-gray-600">
          Note: Certain data may be missing from older profiles
        </p>
        </h4>
        {/* <button className="linear flex items-center justify-center rounded-lg bg-lightPrimary p-2 transition duration-200 hover:cursor-pointer hover:bg-gray-100 dark:bg-navy-700 dark:hover:bg-white/20 dark:active:bg-white/10">
          <div className="flex h-5 w-5 items-center justify-center rounded-full bg-brand-500 text-white dark:bg-white dark:!text-navy-900">
            <AiOutlinePlus />
          </div>
        </button> */}
      </div>
      {/* MasterCard */}
      {/* <div className="mt-[25px] px-[5px] md:px-40 xl:px-1">
        <MasterCard number="1275 XXXX XXXX XXXX" CCV="12X" expDate="02/23" />
      </div> */}

      {/* Card bellow info */}
      {/* <div className="mt-11 w-full rounded-xl bg-lightPrimary px-3 py-3 dark:bg-navy-700">
        <h5 className="text-sm font-bold text-navy-700 dark:text-white">
          Use for added security online
        </h5>
        <p className="text-sm font-medium text-gray-600">
          The card number is refreshed automatically after each use. Each number
          cand be used only once.
        </p>
      </div> */}

      {/* Buttons */}
      {/* <div className="mt-10 flex w-full justify-between px-3 md:justify-around xl:justify-between">
        <div className="flex flex-col items-center justify-center">
          <button className="linear flex items-center justify-center rounded-full bg-white p-3 text-2xl text-brand-500 shadow-2xl transition duration-200 hover:cursor-pointer hover:!bg-gray-50 active:!bg-gray-100 dark:!bg-navy-700 dark:text-white dark:hover:!bg-white/20 dark:active:!bg-white/10">
            <MdCached />
          </button>
          <h5 className="mt-1 text-sm font-medium text-navy-700 dark:text-white">
            Transfer
          </h5>
        </div>

        <div className="flex flex-col items-center justify-center">
          <button className="linear flex items-center justify-center rounded-full bg-white p-3 text-2xl text-yellow-500 shadow-2xl transition duration-200 hover:cursor-pointer hover:!bg-gray-50 active:!bg-gray-100 dark:!bg-navy-700 dark:text-white dark:hover:!bg-white/20 dark:active:!bg-white/10">
            <MdAdd />
          </button>
          <h5 className="mt-1 text-sm font-medium text-navy-700 dark:text-white">
            Top Up
          </h5>
        </div>

        <div className="flex flex-col items-center justify-center">
          <button className="linear flex items-center justify-center rounded-full bg-white p-3 text-2xl text-green-500 shadow-2xl transition duration-200 hover:cursor-pointer hover:!bg-gray-50 active:!bg-gray-100 dark:!bg-navy-700 dark:text-white dark:hover:!bg-white/20 dark:active:!bg-white/10">
            <MdAttachMoney />
          </button>
          <h5 className="mt-2 text-sm font-medium text-navy-700 dark:text-white">
            Pay Bills
          </h5>
        </div>

        <div className="flex flex-col items-center justify-center">
          <button className="linear flex items-center justify-center rounded-full bg-white p-3 text-2xl text-yellow-500 shadow-2xl transition duration-200 hover:cursor-pointer hover:!bg-gray-50 active:!bg-gray-100 dark:!bg-navy-700 dark:text-white dark:hover:!bg-white/20 dark:active:!bg-white/10">
            <MdMoreHoriz />
          </button>
          <h5 className="mt-1 text-sm font-medium text-navy-700 dark:text-white">
            More
          </h5>
        </div>
      </div> */}
      {/* Transactions */}
      {/* <h5 className="mt-[42px] ml-2 text-lg font-bold text-navy-700 dark:text-white">
        Your transactions
      </h5> */}

      <div className="mt-5 flex-1 overflow-y-auto pr-2">
        {/* Group entries by category */}
        {['Objectives', 'Goals', 'Sources', 'Timeline', 'Communication', 'Field', 'Level', 'Background'].map(category => {
          const categoryEntries = profileStats.topEntries.filter(entry => entry.category === category);
          if (categoryEntries.length === 0) return null;
          
          const currentPage = getCurrentPage(category);
          const totalPages = Math.ceil(categoryEntries.length / ITEMS_PER_PAGE);
          const paginatedItems = getPaginatedItems(category);
          const showNavigation = categoryEntries.length > ITEMS_PER_PAGE;
          
          return (
            <div key={category} className="mb-6">
              {/* Category Header with Navigation */}
              <div className="mb-3 pb-2 px-4 border-b border-gray-200 dark:border-navy-600 mx-2">
                <div className="flex items-center justify-between">
                  <h6 className="text-sm font-bold text-navy-700 dark:text-white uppercase tracking-wider">
                    {category}
                  </h6>
                  
                  {showNavigation && (
                    <div className="flex items-center space-x-2">
                      <span className="text-xs text-gray-500 dark:text-gray-400">
                        {currentPage + 1} / {totalPages}
                      </span>
                      <button
                        onClick={() => navigatePage(category, 'prev')}
                        disabled={currentPage === 0}
                        className="p-1 rounded hover:bg-gray-100 dark:hover:bg-navy-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      >
                        <MdChevronLeft className="text-sm text-gray-600 dark:text-gray-400" />
                      </button>
                      <button
                        onClick={() => navigatePage(category, 'next')}
                        disabled={currentPage >= totalPages - 1}
                        className="p-1 rounded hover:bg-gray-100 dark:hover:bg-navy-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      >
                        <MdChevronRight className="text-sm text-gray-600 dark:text-gray-400" />
                      </button>
                    </div>
                  )}
                </div>
              </div>
              
              {/* Category Items */}
              <div className="space-y-4">
                {paginatedItems.map(({ label, count }) => (
                  <Transaction
                    key={`${category}-${label}`}
                    title={label}
                    date={category}
                    sum={count.toString()}
                    mb="mb-0"
                    icon={getCustomIcon(label)}
                  />
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </Card>
  );
}

export default ProfileInsights;
